/* ============================================
   FIND IN STORE — Centered Modal Popup
   Colors Dress — Storefront API + Google Places + Preloading
   ============================================ */

(function () {
  'use strict';

  const modal = document.getElementById('fis-modal-overlay');
  if (!modal) return;

  // ---- CONFIG ----
  const CONFIG = {
    shopDomain: modal.dataset.shopDomain || '',
    storefrontToken: modal.dataset.storefrontToken || '',
    metaobjectType: 'store_locator',
    pageSize: 250,
    initialStoreCount: 4,
    searchResultCount: 10,
    nearbyThreshold: 100, // miles — if nearest store is beyond this, use country/state fallback
    defaultCenter: { lat: 40.7241, lng: -74.0344 }
  };

  // ---- STATE ----
  let allStores = [];
  let storesFetched = false;
  let fetchStoresPromise = null;
  let autocomplete = null;

  let cachedIpLocation = null;
  let ipFetchPromise = null;

  // ---- DOM ----
  const triggerBtn = document.getElementById('fis-trigger-btn');
  const closeBtn = document.getElementById('fis-close-btn');
  const searchInput = document.getElementById('fis-search-input');
  const searchBtn = document.getElementById('fis-search-btn');
  const storesLabel = document.getElementById('fis-stores-label');
  const resultsContainer = document.getElementById('fis-results');

  // ============================================
  // STOREFRONT API — Fetch all stores
  // ============================================

  function normalizeCountry(raw) {
    if (!raw) return '';
    const val = raw.trim();
    const v = val.toLowerCase();

    if (/^(us|usa|u\.s\.a?\.?|united states of america|united states|america)$/i.test(val)) return 'United States';
    if (/^(ksa|saudi|kingdom of saudi arabia|saudi arabia)$/i.test(val)) return 'Saudi Arabia';
    if (/^(trinidad|trinidad and tobago|trinidad & tobago|trinidad-tobago)$/i.test(val)) return 'Trinidad and Tobago';
    if (v === 'costa rica')  return 'Costa Rica';
    if (v === 'australia')   return 'Australia';
    if (v === 'brazil')      return 'Brazil';
    if (v === 'canada')      return 'Canada';
    if (v === 'chile')       return 'Chile';
    if (v === 'guatemala')   return 'Guatemala';
    if (v === 'israel')      return 'Israel';
    if (v === 'kuwait')      return 'Kuwait';
    if (v === 'lebanon')     return 'Lebanon';
    if (v === 'malta')       return 'Malta';
    if (v === 'mexico')      return 'Mexico';
    if (v === 'paraguay')    return 'Paraguay';

    return val;
  }

  function parseStore(node) {
    const fields = {};
    node.fields.forEach(f => { fields[f.key] = f.value; });
    return {
      id: node.id,
      name: fields.store_name || '',
      address: fields.address || '',
      address2: fields.address_2 || '',
      city: fields.city || '',
      state: fields.state || '',
      zip: fields.zip_code || '',
      country: normalizeCountry(fields.country || ''),
      phone: fields.phone || '',
      website: fields.website || '',
      lat: parseFloat(fields.latitude) || null,
      lng: parseFloat(fields.longitude) || null,
      isVisible: ['true', '1', 'yes'].includes((fields.is_visible || '').trim().toLowerCase()),
      distance: null
    };
  }

  async function fetchAllStores() {
    if (storesFetched) return allStores;
    if (fetchStoresPromise) return fetchStoresPromise;

    fetchStoresPromise = (async () => {
      let entries = [];
      let hasNextPage = true;
      let cursor = null;

      while (hasNextPage) {
        const afterClause = cursor ? `, after: "${cursor}"` : '';
        const query = `{
          metaobjects(type: "${CONFIG.metaobjectType}", first: ${CONFIG.pageSize}${afterClause}) {
            edges { cursor node { id handle fields { key value } } }
            pageInfo { hasNextPage }
          }
        }`;

        try {
          const resp = await fetch(
            `https://${CONFIG.shopDomain}/api/2026-01/graphql.json`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'X-Shopify-Storefront-Access-Token': CONFIG.storefrontToken
              },
              body: JSON.stringify({ query })
            }
          );
          const data = await resp.json();
          if (data.errors) { console.error('FIS API error:', data.errors); break; }
          const edges = data.data.metaobjects.edges;
          entries = entries.concat(edges.map(e => parseStore(e.node)));
          hasNextPage = data.data.metaobjects.pageInfo.hasNextPage;
          if (hasNextPage && edges.length > 0) cursor = edges[edges.length - 1].cursor;
        } catch (err) {
          console.error('FIS fetch error:', err);
          break;
        }
      }

      allStores = entries.filter(s => s.isVisible && s.lat && s.lng);
      storesFetched = true;
      return allStores;
    })();

    return fetchStoresPromise;
  }

  // ============================================
  // GEOLOCATION — Shopify-native (no external APIs)
  //
  // External IP APIs (ipapi.co, ipwho.is, etc.) are blocked by
  // Shopify's proxy. These three sources are all same-origin or
  // already authenticated, so they always work on Shopify storefronts.
  //
  // Source priority:
  //   1. window.Shopify.country   — theme-injected, zero fetch
  //   2. /browsing_context_suggestions.json — Shopify built-in endpoint
  //   3. Storefront API localization query   — already authenticated
  //
  // All three return a country ISO code (e.g. "US", "SA").
  // COUNTRY_CENTERS maps that to approximate lat/lng so distance
  // sorting still works when the modal first opens.
  // ============================================

  // Approximate geographic centers for every country with stores.
  // Used to sort stores by distance when only a country code is known.
  const COUNTRY_CENTERS = {
    AU: { lat: -25.27, lng: 133.78, country: 'Australia' },
    BR: { lat: -14.24, lng: -51.93, country: 'Brazil' },
    CA: { lat:  56.13, lng: -106.35, country: 'Canada' },
    CL: { lat: -35.68, lng:  -71.54, country: 'Chile' },
    CR: { lat:   9.75, lng:  -83.75, country: 'Costa Rica' },
    GT: { lat:  15.78, lng:  -90.23, country: 'Guatemala' },
    IL: { lat:  31.05, lng:   34.85, country: 'Israel' },
    KW: { lat:  29.31, lng:   47.48, country: 'Kuwait' },
    LB: { lat:  33.85, lng:   35.86, country: 'Lebanon' },
    MT: { lat:  35.94, lng:   14.38, country: 'Malta' },
    MX: { lat:  23.63, lng: -102.55, country: 'Mexico' },
    PY: { lat: -23.44, lng:  -58.44, country: 'Paraguay' },
    SA: { lat:  23.89, lng:   45.08, country: 'Saudi Arabia' },
    TT: { lat:  10.69, lng:  -61.22, country: 'Trinidad and Tobago' },
    US: { lat:  40.72, lng:  -74.03, country: 'United States' },
  };

  async function getLocationByIP() {
    if (cachedIpLocation) return cachedIpLocation;
    if (ipFetchPromise) return ipFetchPromise;

    ipFetchPromise = (async () => {

      // Helper: resolve ISO code → cachedIpLocation
      function fromCode(code) {
        if (!code) return null;
        const c = COUNTRY_CENTERS[code.toUpperCase()];
        if (!c) return null;
        cachedIpLocation = { lat: c.lat, lng: c.lng, country: c.country, state: '' };
        return cachedIpLocation;
      }

      // 1. window.Shopify.country — set by Shopify theme, no fetch at all
      try {
        const code = window.Shopify && window.Shopify.country;
        const result = fromCode(code);
        if (result) return result;
      } catch (e) { /* not available */ }

      // 2. /browsing_context_suggestions.json — Shopify's own IP detection,
      //    same-origin so never blocked by CORS or proxy
      try {
        const resp = await fetch('/browsing_context_suggestions.json');
        const data = await resp.json();
        const code = data?.detected_values?.country?.value;
        const result = fromCode(code);
        if (result) return result;
      } catch (e) {
        console.warn('browsing_context_suggestions failed:', e);
      }

      // 3. Storefront API localization query — already authenticated,
      //    same domain as store fetch so always works
      try {
        const resp = await fetch(
          `https://${CONFIG.shopDomain}/api/2026-01/graphql.json`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Shopify-Storefront-Access-Token': CONFIG.storefrontToken
            },
            body: JSON.stringify({ query: '{ localization { country { isoCode name } } }' })
          }
        );
        const data = await resp.json();
        const code = data?.data?.localization?.country?.isoCode;
        const result = fromCode(code);
        if (result) return result;
      } catch (e) {
        console.warn('Storefront localization failed:', e);
      }

      return null; // all failed → openModal() uses CONFIG.defaultCenter
    })();

    return ipFetchPromise;
  }

  // ============================================
  // GOOGLE PLACES AUTOCOMPLETE
  // ============================================

  function initAutocomplete() {
    if (!window.google || !google.maps || !google.maps.places) return;
    if (autocomplete) return;

    autocomplete = new google.maps.places.Autocomplete(searchInput, {
      types: ['geocode'],
      fields: ['geometry', 'address_components', 'formatted_address']
    });

    autocomplete.addListener('place_changed', () => {
      const place = autocomplete.getPlace();
      if (place && place.geometry && place.geometry.location) {
        const lat = place.geometry.location.lat();
        const lng = place.geometry.location.lng();
        let state = '';
        let country = '';
        (place.address_components || []).forEach(c => {
          if (c.types.includes('administrative_area_level_1')) state = c.long_name;
          if (c.types.includes('country')) country = c.long_name;
        });
        handleSearchByCoords({ lat, lng, state, country: normalizeCountry(country) });
      }
    });
  }

  function geocodeAddress(query) {
    return new Promise((resolve) => {
      if (!window.google || !google.maps || !google.maps.Geocoder) { resolve(null); return; }
      const geocoder = new google.maps.Geocoder();
      geocoder.geocode({ address: query }, (results, status) => {
        if (status === 'OK' && results.length > 0) {
          const loc = results[0].geometry.location;
          let state = '';
          let country = '';
          (results[0].address_components || []).forEach(c => {
            if (c.types.includes('administrative_area_level_1')) state = c.long_name;
            if (c.types.includes('country')) country = c.long_name;
          });
          resolve({ lat: loc.lat(), lng: loc.lng(), state, country: normalizeCountry(country) });
        } else {
          resolve(null);
        }
      });
    });
  }

  // ============================================
  // DISTANCE
  // ============================================

  function getDistance(lat1, lng1, lat2, lng2) {
    const R = 3959;
    const dLat = toRad(lat2 - lat1);
    const dLng = toRad(lng2 - lng1);
    const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  }

  function toRad(deg) { return deg * (Math.PI / 180); }

  // ============================================
  // COUNTRY MATCHING
  // ============================================

  function matchesCountry(storeCountry, searchCountry) {
    if (!storeCountry || !searchCountry) return false;
    const a = normalizeCountry(storeCountry).toLowerCase();
    const b = normalizeCountry(searchCountry).toLowerCase();
    return a === b;
  }

  // ============================================
  // US STATE MATCHING
  // ============================================

  const US_STATES = {
    'alabama':'AL','alaska':'AK','arizona':'AZ','arkansas':'AR','california':'CA',
    'colorado':'CO','connecticut':'CT','delaware':'DE','florida':'FL','georgia':'GA',
    'hawaii':'HI','idaho':'ID','illinois':'IL','indiana':'IN','iowa':'IA',
    'kansas':'KS','kentucky':'KY','louisiana':'LA','maine':'ME','maryland':'MD',
    'massachusetts':'MA','michigan':'MI','minnesota':'MN','mississippi':'MS','missouri':'MO',
    'montana':'MT','nebraska':'NE','nevada':'NV','new hampshire':'NH','new jersey':'NJ',
    'new mexico':'NM','new york':'NY','north carolina':'NC','north dakota':'ND','ohio':'OH',
    'oklahoma':'OK','oregon':'OR','pennsylvania':'PA','rhode island':'RI','south carolina':'SC',
    'south dakota':'SD','tennessee':'TN','texas':'TX','utah':'UT','vermont':'VT',
    'virginia':'VA','washington':'WA','west virginia':'WV','wisconsin':'WI','wyoming':'WY',
    'district of columbia':'DC'
  };
  const US_STATES_REV = {};
  Object.entries(US_STATES).forEach(([name, abbr]) => { US_STATES_REV[abbr.toLowerCase()] = name; });

  function matchesState(storeState, searchState) {
    const a = (storeState || '').trim().toLowerCase();
    const b = (searchState || '').trim().toLowerCase();
    if (!a || !b) return false;
    if (a === b) return true;
    const aAbbr = (US_STATES[a] || '').toLowerCase();
    const bAbbr = (US_STATES[b] || '').toLowerCase();
    const aFull = US_STATES_REV[a] || '';
    const bFull = US_STATES_REV[b] || '';
    return a === bAbbr || a === bFull || b === aAbbr || b === aFull;
  }

  // ============================================
  // RENDERING
  // ============================================

  function showLoading() {
    if (storesLabel) storesLabel.style.display = 'none';
    resultsContainer.innerHTML = `
      <div class="fis-loading">
        <div class="fis-spinner"></div>
        <span class="fis-loading-text">Finding stores near you…</span>
      </div>`;
  }

  function showNoResults() {
    if (storesLabel) storesLabel.style.display = 'none';
    resultsContainer.innerHTML = `
      <div class="fis-no-results">
        No stores found near that location.<br>Try a different zip code or city.
      </div>`;
  }

  function escapeHtml(str) {
    if (!str) return '';
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  function renderStoreCard(store) {
    const line1 = [store.address, store.address2].filter(Boolean).join(', ');
    const cityStateZip = [store.city, store.state].filter(Boolean).join(', ') + (store.zip ? ' ' + store.zip : '');
    const dirUrl = `https://www.google.com/maps/dir/?api=1&destination=${store.lat},${store.lng}`;

    let rightCol = '';
    if (store.phone) {
      rightCol += `<p class="fis-store-phone-label">Phone Number</p>`;
      rightCol += `<a href="tel:${escapeHtml(store.phone)}" class="fis-store-phone-link">${escapeHtml(store.phone)}</a>`;
    }
    if (store.distance !== null) {
      rightCol += `<div class="fis-store-distance">${Math.round(store.distance)} mi</div>`;
    }

    return `<div class="fis-store-card">
      <div class="fis-store-top">
        <div class="fis-store-info">
          <h3 class="fis-store-name">${escapeHtml(store.name)}</h3>
          <p class="fis-store-address">
            ${escapeHtml(line1)}<br>
            ${escapeHtml(cityStateZip)}
            ${store.country && store.country !== 'United States' ? '<br>' + escapeHtml(store.country) : ''}
          </p>
        </div>
        ${rightCol ? `<div class="fis-store-right">${rightCol}</div>` : ''}
      </div>
      <div class="fis-store-actions">
        <a href="${dirUrl}" target="_blank" rel="noopener" class="fis-btn-directions">GET DIRECTIONS</a>
      </div>
    </div>`;
  }

  function renderStores(stores, headerText) {
    if (!stores || stores.length === 0) { showNoResults(); return; }
    if (storesLabel) storesLabel.style.display = 'block';
    let html = '';
    if (headerText) html += `<div class="fis-state-header">${escapeHtml(headerText)}</div>`;
    stores.forEach(store => { html += renderStoreCard(store); });
    resultsContainer.innerHTML = html;
  }

  function sortByDistance(stores, center, limit) {
    stores.forEach(s => { s.distance = getDistance(center.lat, center.lng, s.lat, s.lng); });
    stores.sort((a, b) => a.distance - b.distance);
    return limit ? stores.slice(0, limit) : stores;
  }

  // ============================================
  // SEARCH
  // Fallback order:
  //   1. Stores within nearbyThreshold miles
  //   2. Stores in same country
  //   3. Stores in same state/region
  //   4. Globally nearest stores (always shows something)
  // ============================================

  async function handleSearchByCoords(location) {
    showLoading();
    const stores = await fetchAllStores();
    if (stores.length === 0) { showNoResults(); return; }

    const sorted = sortByDistance([...stores], location);
    const nearest = sorted.slice(0, CONFIG.searchResultCount);

    // 1. Nearby stores within threshold
    const closeStores = sorted.filter(s => s.distance <= CONFIG.nearbyThreshold);
    if (closeStores.length > 0) {
      renderStores(closeStores.slice(0, CONFIG.searchResultCount));
      return;
    }

    // 2. Same country
    if (location.country) {
      const countryStores = sorted.filter(s => matchesCountry(s.country, location.country));
      if (countryStores.length > 0) {
        renderStores(
          countryStores.slice(0, CONFIG.searchResultCount),
          `Showing stores in ${location.country}`
        );
        return;
      }
    }

    // 3. Same state / region
    if (location.state) {
      const stateStores = sorted.filter(s => matchesState(s.state, location.state));
      if (stateStores.length > 0) {
        renderStores(
          stateStores.slice(0, CONFIG.searchResultCount),
          `Showing stores in ${location.state}`
        );
        return;
      }
    }

    // 4. Globally nearest — always show something
    renderStores(nearest);
  }

  async function performSearch() {
    const query = (searchInput.value || '').trim();
    if (!query) return;
    showLoading();

    const stores = await fetchAllStores();
    if (stores.length === 0) { showNoResults(); return; }

    const location = await geocodeAddress(query);
    if (location) {
      await handleSearchByCoords(location);
    } else {
      const lq = query.toLowerCase();
      const matches = stores.filter(s =>
        `${s.name} ${s.address} ${s.city} ${s.state} ${s.zip} ${s.country}`.toLowerCase().includes(lq)
      );
      if (matches.length > 0) {
        matches.forEach(s => { s.distance = null; });
        renderStores(matches.slice(0, CONFIG.searchResultCount));
      } else {
        showNoResults();
      }
    }
  }

  // ============================================
  // MODAL OPEN / CLOSE
  // ============================================

  async function openModal() {
    modal.classList.add('active');
    document.body.classList.add('fis-modal-open');
    showLoading();

    if (window.google && google.maps && !autocomplete) {
      try {
        await google.maps.importLibrary('places');
        initAutocomplete();
      } catch (e) { console.warn('Places load error:', e); }
    }

    const [stores, ipLocation] = await Promise.all([
      fetchAllStores(),
      getLocationByIP()
    ]);

    const center = (ipLocation && stores.length > 0) ? ipLocation : CONFIG.defaultCenter;
    if (stores.length > 0) {
      await handleSearchByCoords(center);
    } else {
      showNoResults();
    }
  }

  function closeModal() {
    modal.classList.remove('active');
    document.body.classList.remove('fis-modal-open');
  }

  // ============================================
  // PRELOAD & INITIALIZATION
  // ============================================

  async function init() {
    if (!triggerBtn) return;
    fetchAllStores();
    getLocationByIP();
  }

  // ============================================
  // EVENT LISTENERS
  // ============================================

  if (triggerBtn) {
    triggerBtn.addEventListener('click', (e) => {
      e.preventDefault();
      openModal();
    });
  }

  if (closeBtn) closeBtn.addEventListener('click', closeModal);

  modal.addEventListener('click', (e) => {
    if (e.target === modal) closeModal();
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && modal.classList.contains('active')) closeModal();
  });

  if (searchBtn) {
    searchBtn.addEventListener('click', (e) => {
      e.preventDefault();
      performSearch();
    });
  }

  if (searchInput) {
    searchInput.addEventListener('keypress', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        setTimeout(performSearch, 100);
      }
    });

    searchInput.addEventListener('focus', async () => {
      if (window.google && google.maps && !autocomplete) {
        try {
          await google.maps.importLibrary('places');
          initAutocomplete();
        } catch (e) { console.warn('Places load error:', e); }
      }
    });
  }

  init();

})();